import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120),
  email: z.string().trim().toLowerCase().email("Valid email required").max(255),
  guideSlug: z.string().trim().max(80).optional(),
  sourcePath: z.string().trim().max(500).optional(),
  userAgent: z.string().trim().max(500).optional(),
  utmSource: z.string().trim().max(120).optional().nullable(),
  utmMedium: z.string().trim().max(120).optional().nullable(),
  utmCampaign: z.string().trim().max(120).optional().nullable(),
});

const NOTIFY_TO = "info@comprehensivetaxresolution.com";

export const submitLeadMagnetDownload = createServerFn({ method: "POST" })
  .inputValidator((data) => schema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error } = await supabaseAdmin
      .from("lead_magnet_downloads")
      .insert({
        name: data.name,
        email: data.email,
        guide_slug: data.guideSlug ?? "7-secrets",
        source_path: data.sourcePath ?? null,
        user_agent: data.userAgent ?? null,
        utm_source: data.utmSource ?? null,
        utm_medium: data.utmMedium ?? null,
        utm_campaign: data.utmCampaign ?? null,
      });

    if (error) {
      console.error("lead_magnet_downloads insert failed", error);
      throw new Error("Could not save your request. Please try again.");
    }

    // Push to GHL (best-effort — the Supabase row is the source of truth).
    try {
      const { upsertContact } = await import("./ghl.server");
      await upsertContact({
        name: data.name,
        email: data.email,
        source: "Website (Lead Magnet)",
        tags: [`lead-magnet-${data.guideSlug ?? "7-secrets"}`, "website-lead-magnet"],
        notes: `Downloaded: ${data.guideSlug ?? "7-secrets"}\nPage: ${data.sourcePath ?? "-"}\nUTM: source=${data.utmSource ?? "-"} medium=${data.utmMedium ?? "-"} campaign=${data.utmCampaign ?? "-"}`,
      });
    } catch (e) {
      console.warn("GHL lead magnet push skipped:", (e as Error)?.message);
    }


    // Best-effort email notification via Lovable Emails queue.
    // If the email infrastructure is not yet provisioned, this no-ops and
    // we still return success — the lead is captured in the database.
    try {
      await (supabaseAdmin.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<unknown>)("enqueue_email", {
        queue_name: "transactional_emails",
        message: {
          to: NOTIFY_TO,
          subject: `New 7 Secrets Guide download — ${data.name}`,
          html: `
            <h2>New lead magnet download</h2>
            <p><strong>Name:</strong> ${escapeHtml(data.name)}</p>
            <p><strong>Email:</strong> ${escapeHtml(data.email)}</p>
            <p><strong>Guide:</strong> ${escapeHtml(data.guideSlug ?? "7-secrets")}</p>
            <p><strong>Page:</strong> ${escapeHtml(data.sourcePath ?? "—")}</p>
            <p><strong>UTM:</strong>
              source=${escapeHtml(data.utmSource ?? "")},
              medium=${escapeHtml(data.utmMedium ?? "")},
              campaign=${escapeHtml(data.utmCampaign ?? "")}
            </p>
          `,
          template_name: "lead-magnet-notification",
        },
      });
    } catch (e) {
      // Don't fail the download if email isn't wired up yet.
      console.warn("Email notification skipped:", (e as Error)?.message);
    }

    return { ok: true };
  });

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
